import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Bell, Check, Send, Settings2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import NotificationPrefs from "@/components/NotificationPrefs";

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationInbox() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const seenRef = useRef(null); // Set of notification_ids already shown (null until first load)

  const load = useCallback(async () => {
    try {
      const r = await api.get("/notifications");
      const list = r.data.items || [];
      setItems(list);
      setUnread(r.data.unread || 0);

      // Pop a toast for genuinely-new unread notifications (in-app alert path — works
      // even when the browser has NOT granted push permission). Skip the very first
      // load so we don't toast the whole backlog on page open.
      if (seenRef.current === null) {
        seenRef.current = new Set(list.map((n) => n.notification_id));
      } else {
        const fresh = list.filter((n) => !n.read && !seenRef.current.has(n.notification_id));
        fresh
          .slice()
          .reverse()
          .forEach((n) => {
            toast(n.title, {
              description: n.body || undefined,
              action: n.url
                ? { label: "View", onClick: () => navigate(n.url) }
                : undefined,
            });
          });
        list.forEach((n) => seenRef.current.add(n.notification_id));
      }
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.debug("notifications load failed", e);
    }
  }, [navigate]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    const onRefresh = () => load();
    window.addEventListener("twp:refresh-notifications", onRefresh);
    return () => {
      clearInterval(t);
      window.removeEventListener("twp:refresh-notifications", onRefresh);
    };
  }, [load]);

  const openItem = (n) => {
    setOpen(false);
    if (!n.read) {
      setUnread((u) => Math.max(0, u - 1));
      setItems((prev) => prev.map((x) => (x.notification_id === n.notification_id ? { ...x, read: true } : x)));
      api.post(`/notifications/${n.notification_id}/read`).catch(() => {});
    }
    if (n.url) navigate(n.url);
  };

  const markAll = () => {
    setUnread(0);
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    api.post("/notifications/read-all").catch(() => {});
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/notifications/test");
      await load();
      if (data?.push_devices > 0) {
        toast.success("Test sent — check your desktop and the bell");
      } else {
        toast.success("Test sent to your in-app inbox");
      }
    } catch (e) {
      toast.error("Could not send test notification");
    } finally {
      setTesting(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button className="relative h-8 w-8 flex items-center justify-center rounded-sm text-zinc-500 hover:bg-zinc-200 hover:text-zinc-900 transition-colors"
          data-testid="notification-inbox" aria-label="Notifications" title="Notifications">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[15px] h-[15px] px-1 rounded-full bg-rose-600 text-white text-[9px] font-bold flex items-center justify-center" data-testid="notification-unread">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-200">
          <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Notifications</span>
          <div className="flex items-center gap-2">
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1" data-testid="notification-mark-all">
                <Check className="h-3 w-3" /> Mark all read
              </button>
            )}
            <button
              onClick={() => setShowPrefs((v) => !v)}
              className={`h-6 w-6 flex items-center justify-center rounded-sm transition-colors ${showPrefs ? "bg-zinc-200 text-zinc-900" : "text-zinc-500 hover:bg-zinc-200 hover:text-zinc-900"}`}
              data-testid="notification-prefs-toggle"
              aria-label="Notification preferences"
              title="Notification preferences"
            >
              <Settings2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        {showPrefs ? (
          <NotificationPrefs />
        ) : (
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && <p className="px-4 py-8 text-sm text-zinc-400 text-center">You're all caught up.</p>}
          {items.map((n) => (
            <button key={n.notification_id} onClick={() => openItem(n)}
              className={`w-full text-left px-4 py-3 border-b border-zinc-100 last:border-0 hover:bg-zinc-50 transition-colors ${n.read ? "" : "bg-zinc-50/60"}`}
              data-testid={`notification-item-${n.notification_id}`}>
              <div className="flex items-start gap-2">
                {!n.read && <span className="h-2 w-2 rounded-full bg-rose-500 mt-1.5 shrink-0" />}
                <div className={`min-w-0 ${n.read ? "pl-4" : ""}`}>
                  <p className="text-sm font-medium leading-snug">{n.title}</p>
                  {n.body && <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{n.body}</p>}
                  <p className="text-[10px] text-zinc-400 mt-1">{timeAgo(n.created_at)}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
        )}
        <div className="px-4 py-2.5 border-t border-zinc-200">
          <button onClick={sendTest} disabled={testing}
            className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-zinc-600 hover:text-zinc-900 disabled:opacity-50 transition-colors"
            data-testid="notification-send-test">
            <Send className="h-3 w-3" /> {testing ? "Sending…" : "Send me a test notification"}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
